import { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "wouter";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Send,
  History,
  Paperclip,
  Check,
  Clock,
  MessageSquare,
  Upload,
  X,
  FileIcon,
  ChevronDown,
  ChevronUp,
  Zap
} from "lucide-react";
import { trackEvent } from "@/lib/analytics/posthog";

interface Consultation {
  id: string;
  userId: number;
  subject: string;
  message: string;
  attachments?: { filename: string; size: number; contentType: string }[];
  status: string;
  createdAt: string;
  respondedAt?: string | null;
}

export default function MyConsultationsPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Form state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Tab and history state
  const [activeTab, setActiveTab] = useState<'submit' | 'history'>('submit');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
    }
  }, [isAuthenticated, isLoading, setLocation]);

  // Fetch consultation credits
  const { data: consultData, isLoading: consultLoading, error: consultError } = useQuery({
    queryKey: ['/api/users', user?.id, 'consultation-credits'],
    enabled: !!user?.id,
    retry: false,
  });

  // Fetch consultation history
  const { data: consultationsData, isLoading: consultationsLoading } = useQuery<Consultation[]>({
    queryKey: ['/api/consultations/user', user?.id],
    enabled: !!user?.id && activeTab === 'history',
    retry: false,
  });

  useEffect(() => {
    if (consultError) {
      toast({
        title: 'Session Error',
        description: 'Your session has expired. Please log in again.',
        variant: 'destructive',
      });
      setLocation('/login');
    }
  }, [consultError, toast, setLocation]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (isLoading || consultLoading) return;
    if (!consultData) return;
    try {
      const consultationCreditsValue = (consultData as any)?.consultationCredits ?? 0;
      const planTierValue = (consultData as any)?.planTier || (user as any)?.planTier || 'free';
      trackEvent('consultations_page_viewed', {
        consultation_credits: consultationCreditsValue,
        plan_tier: planTierValue,
      });
    } catch {}
  }, [isAuthenticated, user?.id, isLoading, consultLoading, consultData]);

  if (isLoading || consultLoading) {
    return (
      <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
        <div className="container mx-auto px-6">
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Loading your consultations...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!isAuthenticated) {
    return (
      <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
        <div className="container mx-auto px-6">
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Redirecting to login...</p>
            <div className="mt-4">
              <Link href="/login">
                <Button className="btn-primary rounded-2xl">Go to Login</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const consultationCredits = (consultData as any)?.consultationCredits ?? 0;
  const consultQuota = (consultData as any)?.quota ?? 0;
  const nextConsultReset = (consultData as any)?.nextConsultationResetAt || null;
  const nextConsultResetFormatted = nextConsultReset ? new Date(nextConsultReset).toLocaleDateString() : null;
  const planTier = (consultData as any)?.planTier || (user as any)?.planTier || 'free';

  // Team consultation credits
  const teamConsultationCredits = (consultData as any)?.teamConsultationCredits ?? 0;
  const teamName = (consultData as any)?.teamName || null;
  const totalAvailable = (consultData as any)?.totalAvailable ?? (consultationCredits + teamConsultationCredits);

  const consultations = consultationsData || [];

  const onFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setSelectedFiles(files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const totalSelectedSizeMB = selectedFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);

  const submitConsultation = async () => {
    if (!subject.trim() || message.trim().length < 10) {
      toast({
        title: 'Invalid input',
        description: 'Please provide a subject and a message of at least 10 characters.',
        variant: 'destructive',
      });
      return;
    }
    if (!user?.id) {
      toast({ title: 'Not logged in', description: 'Please log in again.', variant: 'destructive' });
      return;
    }
    if (totalSelectedSizeMB > 20) {
      toast({ title: 'Attachments too large', description: 'Total size must be less than 20 MB.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('userId', String(user.id));
      fd.append('subject', subject.trim());
      fd.append('message', message.trim());
      selectedFiles.forEach((f) => fd.append('attachments', f));

      const res = await fetch('/api/consultations', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!res.ok) {
        let msg = res.statusText;
        try {
          const data = await res.json();
          msg = data.message || msg;
        } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      if (data && data.emailSent === false) {
        throw new Error('Email failed to send. Please try again later or contact support@bouncebunny.app.');
      }
      try {
        trackEvent('consultation_submitted', {
          has_attachments: selectedFiles.length > 0,
          attachment_count: selectedFiles.length,
          total_size_mb: Number(totalSelectedSizeMB.toFixed(1)),
          subject_length: subject.trim().length,
          message_length: message.trim().length,
        });
      } catch {}
      toast({ title: 'Consultation submitted!', description: 'We will reply within 24 hours.' });
      setSubject('');
      setMessage('');
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['/api/users', user?.id, 'consultation-credits'] });
      queryClient.invalidateQueries({ queryKey: ['/api/consultations/user', user?.id] });
    } catch (err: any) {
      toast({ title: 'Submission failed', description: err?.message || 'Please try again later.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              My Consultations
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              Get expert training and recovery guidance within 24 hours
            </p>
          </div>

          {/* Tabbed Interface */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'submit' | 'history')} className="space-y-6">
            <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto glass-card dark:glass-card-dark rounded-2xl p-1">
              <TabsTrigger value="submit" className="flex items-center gap-2 rounded-xl">
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Submit New</span>
                <span className="sm:hidden">Submit</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2 rounded-xl">
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">My History</span>
                <span className="sm:hidden">History</span>
              </TabsTrigger>
            </TabsList>

            {/* Submit New Tab */}
            <TabsContent value="submit" className="space-y-6">
              {/* Enhanced Credit Status Alerts - Shows both personal and team credits */}
              {totalAvailable > 0 ? (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Show personal credits */}
                      <p className="text-green-700 dark:text-green-300 font-semibold">
                        {consultationCredits} personal consultation credit{consultationCredits !== 1 ? 's' : ''}
                      </p>
                      {/* Show team credits if available */}
                      {teamConsultationCredits > 0 && teamName && (
                        <p className="text-green-600 dark:text-green-400 mt-1">
                          + {teamConsultationCredits} team credit{teamConsultationCredits !== 1 ? 's' : ''} from {teamName}
                        </p>
                      )}
                      {/* Show total */}
                      <p className="text-green-800 dark:text-green-200 font-bold mt-2">
                        Total: {totalAvailable} available
                      </p>
                      {teamConsultationCredits > 0 && (
                        <p className="text-xs text-green-600/80 dark:text-green-400/80 mt-1">
                          Team credits are used first
                        </p>
                      )}
                      {nextConsultResetFormatted && (
                        <p className="text-sm text-green-700/80 dark:text-green-300/80 mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Resets {nextConsultResetFormatted}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-2xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-orange-700 dark:text-orange-300 font-semibold">
                        {planTier === 'free' && !teamName ? 'Consultations available on paid plans' : 'No credits remaining'}
                      </p>
                      <div className="mt-2">
                        <Link href="/pricing">
                          <Button className="btn-primary rounded-2xl text-sm">
                            Upgrade Plan
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Enhanced Form Card */}
              <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg p-8">
                <div className="space-y-4">
                  <Input
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="rounded-2xl"
                  />
                  <textarea
                    placeholder="Describe your training or recovery question..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    className="w-full rounded-2xl p-3 bg-transparent border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
                  />

                  {/* Visual File Upload UI */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium mb-2">
                      <Paperclip className="inline w-4 h-4 mr-2" />
                      Attachments (optional)
                    </label>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*,application/pdf"
                      multiple
                      onChange={onFilesChange}
                      className="hidden"
                      id="file-upload"
                    />

                    <label
                      htmlFor="file-upload"
                      className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-2xl cursor-pointer hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
                    >
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Click to upload images, videos, or PDFs
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Max 20 MB total</p>
                      </div>
                    </label>

                    {/* File Preview Grid */}
                    {selectedFiles.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="glass-card dark:glass-card-dark rounded-xl p-3 relative group">
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <FileIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-xs text-gray-600 dark:text-gray-300 truncate text-center">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500 text-center mt-1">
                              {(file.size / (1024 * 1024)).toFixed(1)} MB
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {totalSelectedSizeMB > 0 && (
                      <p className={`text-xs ${totalSelectedSizeMB > 20 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                        Total size: {totalSelectedSizeMB.toFixed(1)} MB / 20 MB
                      </p>
                    )}
                  </div>

                  {/* Enhanced Submit Button */}
                  <div>
                    <Button
                      onClick={submitConsultation}
                      disabled={isSubmitting || totalAvailable <= 0}
                      className="w-full btn-primary rounded-2xl py-3 text-lg font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Submit Consultation
                        </>
                      )}
                    </Button>
                  </div>

                  {consultQuota > 0 && (
                    <p className="text-xs text-gray-500">Your plan quota: {consultQuota} consultations per billing cycle.</p>
                  )}
                </div>
              </Card>
            </TabsContent>

            {/* My History Tab */}
            <TabsContent value="history" className="space-y-6">
              {consultationsLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Loading consultation history...</p>
                </div>
              ) : consultations.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-200 dark:bg-[var(--dark-3)] rounded-full flex items-center justify-center mx-auto mb-6">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">No Consultations Yet</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    Submit your first training or recovery question to get expert guidance.
                  </p>
                  <Button
                    onClick={() => setActiveTab('submit')}
                    className="btn-primary px-8 py-3 rounded-2xl"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit Your First Question
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {consultations.map((consultation, index) => (
                    <Card
                      key={consultation.id}
                      className="glass-card dark:glass-card-dark rounded-3xl shadow-lg card-hover overflow-hidden"
                    >
                      <CardContent className="p-6">
                        {/* Date */}
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <Clock className="w-4 h-4" />
                          {formatDate(consultation.createdAt)}
                        </div>

                        {/* Subject */}
                        <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">
                          {consultation.subject}
                        </h3>

                        {/* Message Preview */}
                        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-4">
                          {consultation.message}
                        </p>

                        {/* Attachments & Response Info */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
                          <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                            {consultation.attachments && consultation.attachments.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Paperclip className="w-4 h-4" />
                                <span>{consultation.attachments.length} file{consultation.attachments.length > 1 ? 's' : ''}</span>
                              </div>
                            )}
                            {consultation.respondedAt && (
                              <div className="flex items-center gap-1 text-green-600">
                                <Check className="w-4 h-4" />
                                <span>Responded {formatDate(consultation.respondedAt)}</span>
                              </div>
                            )}
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedId(consultation.id === expandedId ? null : consultation.id)}
                            className="rounded-xl"
                          >
                            {consultation.id === expandedId ? (
                              <>
                                <ChevronUp className="w-4 h-4 mr-1" />
                                Less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4 mr-1" />
                                Details
                              </>
                            )}
                          </Button>
                        </div>

                        {/* Expanded Details */}
                        {consultation.id === expandedId && (
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="bg-gray-50 dark:bg-[var(--dark-3)] rounded-2xl p-4">
                              <h4 className="text-sm font-medium mb-2">Full Message</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                {consultation.message}
                              </p>
                            </div>

                            {consultation.attachments && consultation.attachments.length > 0 && (
                              <div className="mt-3">
                                <h4 className="text-sm font-medium mb-2">Attachments</h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {consultation.attachments.map((attachment, idx) => (
                                    <div key={idx} className="glass-card dark:glass-card-dark rounded-xl p-2 text-xs">
                                      <FileIcon className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                                      <p className="truncate text-center">{attachment.filename}</p>
                                      <p className="text-center text-gray-500 mt-1">
                                        {(attachment.size / (1024 * 1024)).toFixed(1)} MB
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
